import {
    createContext,
    useContext,
    ReactNode,
    useMemo,
    useCallback,
} from "react";
import { useStoreQuery, useUpdateStoreMutation } from "../../datastore/StoreDataStore";
import { Store } from "../../../../common/models";
import { GenericFormReturn } from "../../common/hooks/useGenericForm";
import { StoreEditFormData, useStoreEditForm } from "./hooks/useStoreEditForm";

export interface StoreEditDetailsContextType extends GenericFormReturn<StoreEditFormData> {
    store: Store | null;
    storeLoading: boolean;
    storeError: boolean;
    submitForm: () => void;
    submitFormLoading: boolean;
    submitFormError: any;
    submitFormCalled: boolean;
}

const StoreEditDetailsContext = createContext<StoreEditDetailsContextType | undefined>(undefined);

export const useStoreEditDetailsContext = (): StoreEditDetailsContextType => {
    const context = useContext(StoreEditDetailsContext);
    if (!context) {
        throw new Error(
            "useStoreEditDetailsContext must be used within a StoreEditProvider"
        );
    }
    return context;
};

export interface StoreEditProviderProps {
    children: ReactNode;
    storeId: string;
}

export const StoreEditProvider = ({
    children,
    storeId,
}: StoreEditProviderProps): JSX.Element => {
    const {
        data: store,
        loading: storeLoading,
        error: storeError,
    } = useStoreQuery(storeId);

    const [
        updateStore,
        {
            loading: submitFormLoading,
            error: submitFormError,
            called: submitFormCalled,
            data: updateStoreData,
        },
    ] = useUpdateStoreMutation();

    const initialFormData = useMemo(() => ({
        storeName: updateStoreData?.name || store?.name || "",
    }), [updateStoreData?.name, store?.name]);

    const submitOperationConfig = useMemo(() => ({
        loading: submitFormLoading,
        called: submitFormCalled,
        error: submitFormError,
        successMessage: "Store was updated successfully.",
        failureMessage: "Error updating store. Check console logs.",
        onSuccess: () => { },
    }), [submitFormLoading, submitFormCalled, submitFormError]);

    // All form logic is now handled by the useStoreEditForm hook
    const storeEditForm = useStoreEditForm(initialFormData.storeName, submitOperationConfig);

    const submitForm = useCallback(() => {
        updateStore({ ...store, name: storeEditForm.formValues.storeName } as Store);
    }, [updateStore, storeEditForm.formValues.storeName, store]);

    // Provide complete context value with both legacy and modern APIs
    const contextValue: StoreEditDetailsContextType = {
        ...storeEditForm,
        store,
        storeLoading,
        storeError,
        submitFormLoading,
        submitFormError,
        submitFormCalled,
        submitForm,
    };

    return (
        <StoreEditDetailsContext.Provider value={contextValue}>
            {children}
        </StoreEditDetailsContext.Provider>
    );
};
